// src/modules/app-views/_apps/applekkus/landingPages/service.js
// *******************************************************************************
// LANDING PAGES SERVICE
// *******************************************************************************
// Service layer for landing page operations including CRUD, AI generation,
// and Netlify deployment (placeholder endpoints).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const BASE_URL = '/v1/landing-pages';

// *******************************************************************************
// API METHODS
// *******************************************************************************

async function getById(id) {
  const url = `${BASE_URL}/${id}`;

  const [response] = await axiosService.get({
    url,
    returnAsObject: true,
  });

  return response;
}

async function getPaginated(params = {}) {
  const [response] = await axiosService.get({
    url: `${BASE_URL}/pagination`,
    params,
    returnAsObject: true,
  });

  return response;
}

async function create(data) {
  const [response] = await axiosService.post({
    url: BASE_URL,
    data,
    returnAsObject: true,
  });

  return response;
}

async function update(id, data) {
  const url = `${BASE_URL}/${id}`;

  const [response] = await axiosService.put({
    url,
    data,
    returnAsObject: true,
  });

  return response;
}

async function remove(id) {
  const url = `${BASE_URL}/${id}`;

  const [response] = await axiosService.delete({
    url,
    returnAsObject: true,
  });

  return response;
}

// *******************************************************************************
// AI & DEPLOY METHODS (PLACEHOLDER)
// *******************************************************************************

async function generateWithAI(id, prompt) {
  const msg = await appService.message({
    flag: 'loading',
    text: 'generating_landing_page',
    delay: 0,
  });

  try {
    const url = `${BASE_URL}/${id}/generate`;

    const [response] = await axiosService.post({
      url,
      data: { prompt },
      returnAsObject: true,
    });

    if (response?.success) {
      msg.update({
        flag: 'success',
        text: translateX('landing_page_generated'),
        delay: 3,
      });
    } else {
      msg.update({
        flag: 'error',
        text: response?.message || translateX('generation_failed'),
        delay: 5,
      });
    }

    return response;
  } catch (error) {
    msg.update({
      flag: 'error',
      text: error?.message || translateX('generation_failed'),
      delay: 5,
    });
    throw error;
  }
}

async function deployToNetlify(id) {
  const msg = await appService.message({
    flag: 'loading',
    text: 'deploying_to_netlify',
    delay: 0,
  });

  try {
    const url = `${BASE_URL}/${id}/deploy`;

    const [response] = await axiosService.post({
      url,
      returnAsObject: true,
    });

    if (response?.success) {
      msg.update({
        flag: 'success',
        text: translateX('deploy_success'),
        delay: 3,
      });
    } else {
      msg.update({
        flag: 'error',
        text: response?.message || translateX('deploy_failed'),
        delay: 5,
      });
    }

    return response;
  } catch (error) {
    msg.update({
      flag: 'error',
      text: error?.message || translateX('deploy_failed'),
      delay: 5,
    });
    throw error;
  }
}

async function getTemplates() {
  const [response] = await axiosService.get({
    url: `${BASE_URL}/templates`,
    returnAsObject: true,
  });

  return response;
}

// *******************************************************************************
// SERVICE
// *******************************************************************************
const landingPagesService = {
  // CRUD
  getById,
  getPaginated,
  create,
  update,
  remove,

  // AI & Deploy
  generateWithAI,
  deployToNetlify,
  getTemplates,
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default landingPagesService;
